import { apiService } from "../../app.service";

export const labelApi = apiService.injectEndpoints({
  endpoints: (builder) => ({
    // ==================== ALLERGEN ENDPOINTS ====================
    fetchAllergens: builder.query({
      query: () => ({
        url: "/label/allergens",
      }),
      providesTags: ["Allergen"],
    }),
    
    createAllergen: builder.mutation({
      query: (data) => ({
        url: "/label/allergens",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Allergen"],
    }),
    
    updateAllergen: builder.mutation({
      query: ({ id, data }) => ({
        url: `/label/allergens/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: "Allergen", id }],
    }),
    
    deleteAllergen: builder.mutation({
      query: (id) => ({
        url: `/label/allergens/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Allergen"],
    }),

    // ==================== CATEGORY ENDPOINTS ====================
    fetchCategories: builder.query({
      query: () => ({
        url: "/label/categories",
      }),
      providesTags: ["Category"],
    }),
    
    fetchCategory: builder.query({
      query: (id) => ({
        url: `/label/categories/${id}`,
      }),
      providesTags: (result, error, id) => [{ type: "Category", id }],
    }),
    
    createCategory: builder.mutation({
      query: (data) => ({
        url: "/label/categories",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Category"],
    }),
    
    updateCategory: builder.mutation({
      query: ({ id, data }) => ({
        url: `/label/categories/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: "Category", id }],
    }),
    
    deleteCategory: builder.mutation({
      query: (id) => ({
        url: `/label/categories/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Category"],
    }),

    // ==================== PRODUCT ENDPOINTS ====================
    fetchProducts: builder.query({
      query: ({ categoryId, search } = {}) => {
        let url = "/label/products";
        const params = new URLSearchParams();
        if (categoryId) params.append("categoryId", categoryId);
        if (search) params.append("search", search);
        if (params.toString()) url += `?${params.toString()}`;
        return { url };
      },
      providesTags: ["Product"],
    }),
    
    fetchProductsByCategory: builder.query({
      query: (categoryId) => ({
        url: `/label/products/category/${categoryId}`,
      }),
      providesTags: (result, error, categoryId) => [
        { type: "Product", categoryId },
      ],
    }),
    
    fetchProduct: builder.query({
      query: (id) => ({
        url: `/label/products/${id}`,
      }),
      providesTags: (result, error, id) => [{ type: "Product", id }],
    }),
    
    createProduct: builder.mutation({
      query: (data) => ({
        url: "/label/products",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Product"],
    }),
    
    updateProduct: builder.mutation({
      query: ({ id, data }) => ({
        url: `/label/products/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: "Product", id }],
    }),
    
    deleteProduct: builder.mutation({
      query: (id) => ({
        url: `/label/products/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Product"],
    }),

    // ==================== PRINT QUEUE ENDPOINTS ====================
    fetchPrintQueue: builder.query({
      query: ({ printed = false } = {}) => ({
        url: `/label/print-queue?printed=${printed}`,
      }),
      providesTags: ["PrintQueue"],
    }),
    
    addToPrintQueue: builder.mutation({
      query: (data) => ({
        url: "/label/print-queue",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["PrintQueue"],
    }),
    
    removeFromPrintQueue: builder.mutation({
      query: (id) => ({
        url: `/label/print-queue/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["PrintQueue"],
    }),
    
    clearPrintQueue: builder.mutation({
      query: () => ({
        url: "/label/print-queue/clear/all",
        method: "DELETE",
      }),
      invalidatesTags: ["PrintQueue"],
    }),
    
    printLabels: builder.mutation({
      query: (data) => ({
        url: "/label/print",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["PrintQueue"],
    }),
  }),
});

export const {
  // Allergen hooks
  useFetchAllergensQuery,
  useCreateAllergenMutation,
  useUpdateAllergenMutation,
  useDeleteAllergenMutation,
  
  // Category hooks
  useFetchCategoriesQuery,
  useFetchCategoryQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
  
  // Product hooks
  useFetchProductsQuery,
  useFetchProductsByCategoryQuery,
  useFetchProductQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
  
  // Print Queue hooks
  useFetchPrintQueueQuery,
  useAddToPrintQueueMutation,
  useRemoveFromPrintQueueMutation,
  useClearPrintQueueMutation,
  usePrintLabelsMutation,
} = labelApi;